import { useMemo, useState } from 'react'

import {
  CourtCasesListSortInput,
  CourtCaseState,
  MessageType,
  SortDirection
} from '@app/graphql/types'
import { usePersistedState } from '@app/lib/hooks/usePersistedState'
import { CourtCasesQuery, useCourtCasesQuery } from '@app/queries/CourtCases/CourtCases.queries'

export const CourtCasesStateFilterValues = ['Open', 'Closed']

export type CourtCasesFilterValues = {
  state?: typeof CourtCasesStateFilterValues[number]
}

export type CourtCaseItem = CourtCasesQuery['courtCases']['items'][number] & {
  actionRequired?: boolean
}

export const useCourtCasesTable = () => {
  const [showFilter, setShowFilter] = usePersistedState('user-filter:CourtCases', false)
  const [filterValues, setFilterValues] = useState<Partial<CourtCasesFilterValues>>({})
  const [sorts, setSorts] = useState<Partial<CourtCasesListSortInput>>({
    createdAt: SortDirection.Desc
  })

  const query = useMemo(() => {
    return {
      fetchPolicy: 'cache-and-network' as const,
      variables: {
        filters: {
          state: parseStateFilter(filterValues.state),
          includeUpcomingAppointment: { equals: true }
        },
        sorts
      }
    }
  }, [filterValues, sorts])

  const { data, loading, error } = useCourtCasesQuery(query)

  const entries = (data?.courtCases.items || []).map((item) => ({
    ...item,
    actionRequired: item.messages.last?.type === MessageType.CourtCaseCustomer
  }))

  return {
    entries,
    loading,
    error,
    filterValues,
    setFilterValues,
    showFilter,
    setShowFilter,
    sorts,
    setSorts
  }
}

const parseStateFilter = (value: CourtCasesFilterValues['state']) => {
  switch (value) {
    case 'Open':
      return {
        in: [
          CourtCaseState.ClientFileApproved,
          CourtCaseState.ClientFileOpenedByLawyer,
          CourtCaseState.UpdatedByCustomer,
          CourtCaseState.UpdatedByLawyer
        ]
      }
    case 'Closed':
      return {
        equals: CourtCaseState.Settled
      }
    default:
      return {
        in: [
          CourtCaseState.ClientFileApproved,
          CourtCaseState.ClientFileOpenedByLawyer,
          CourtCaseState.UpdatedByCustomer,
          CourtCaseState.UpdatedByLawyer,
          CourtCaseState.Settled
        ]
      }
  }
}
